import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Layers, Plus, Trash2 } from "lucide-react";
import AmountInput from "@/components/AmountInput";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const METHODS_WITHOUT_POLIZZA = [];

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

let _rowSeq = 0;
function newRow(contraente = "") {
  _rowSeq += 1;
  return {
    _rid: `r${Date.now()}-${_rowSeq}`,
    contraente,
    numero_polizza: "",
    importo: "",
    abbuono: "",
    compagnia_id: "",
  };
}

/**
 * MultiPolizzaDialog — Registra un "incasso multiplo": 1 transazione fisica (stesso metodo)
 * che copre N polizze. Il totale è calcolato in automatico come somma delle polizze.
 */
export default function MultiPolizzaDialog({
  open,
  onOpenChange,
  sheetId,
  methods = [],
  allowAbbuoni = false,
  plurimandatari = false,
  companies = [],
  defaultMethod = "Contante",
  onSuccess,
}) {
  const [metodo, setMetodo] = useState(defaultMethod);
  const [urgente, setUrgente] = useState(false);
  const [note, setNote] = useState("");
  const [rows, setRows] = useState([newRow(), newRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setMetodo(defaultMethod || methods[0] || "Contante");
      setUrgente(false);
      setNote("");
      setRows([newRow(), newRow()]);
      setError("");
    }
  }, [open, defaultMethod, methods]);

  const polizzaRequired = !METHODS_WITHOUT_POLIZZA.includes(metodo);
  const activeCompanies = companies.filter((c) => c.attivo);

  // Autofill contraente: se le righe successive sono vuote, propaga il valore della prima.
  const patchRow = (idx, patch) => {
    setRows((r) => {
      const next = r.map((row, i) => (i === idx ? { ...row, ...patch } : row));
      if (idx === 0 && "contraente" in patch) {
        // Se il contraente delle righe successive coincide col vecchio valore della prima
        // (o è vuoto), lo aggiorniamo. Se l'utente l'ha già cambiato, non tocchiamo.
        const oldContraente = (r[0]?.contraente || "").trim();
        const newContraente = (patch.contraente || "").trim();
        for (let i = 1; i < next.length; i++) {
          const cur = (next[i].contraente || "").trim();
          if (cur === "" || cur === oldContraente) {
            next[i] = { ...next[i], contraente: newContraente };
          }
        }
      }
      return next;
    });
  };

  const addRow = () => {
    setRows((r) => [...r, newRow(r[0]?.contraente || "")]);
  };
  const removeRow = (idx) => {
    setRows((r) => (r.length <= 2 ? r : r.filter((_, i) => i !== idx)));
  };

  const totale = rows.reduce((acc, r) => {
    const v = parseFloat(String(r.importo || "0").replace(",", "."));
    return acc + (isFinite(v) ? v : 0);
  }, 0);
  const totaleAbbuoni = rows.reduce((acc, r) => {
    const v = parseFloat(String(r.abbuono || "0").replace(",", "."));
    return acc + (isFinite(v) ? v : 0);
  }, 0);

  const canSubmit =
    rows.length >= 2 &&
    rows.every((r) => {
      const imp = parseFloat(String(r.importo || "").replace(",", "."));
      if (!isFinite(imp) || imp <= 0) return false;
      if (!(r.contraente || "").trim()) return false;
      if (polizzaRequired && !(r.numero_polizza || "").trim()) return false;
      if (plurimandatari && !r.compagnia_id) return false;
      return true;
    });

  const submit = async () => {
    setError("");
    if (!canSubmit) {
      setError("Compila tutte le righe (contraente, polizza, importo > 0) e la compagnia se richiesta.");
      return;
    }
    const polizze = rows.map((r) => ({
      contraente: (r.contraente || "").trim(),
      numero_polizza: (r.numero_polizza || "").trim(),
      importo: parseFloat(String(r.importo).replace(",", ".")),
      abbuono: parseFloat(String(r.abbuono || "0").replace(",", ".")) || 0,
      compagnia_id: plurimandatari ? (r.compagnia_id || null) : null,
    }));
    setSubmitting(true);
    try {
      const resp = await api.post(`/cash-sheets/${sheetId}/entries/multi`, {
        metodo_pagamento: metodo,
        note: note.trim(),
        urgente,
        polizze,
      });
      toast.success(`Incasso multiplo registrato (${polizze.length} polizze, ${formatEUR(totale)})`);
      onOpenChange?.(false);
      onSuccess?.(resp.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="multi-polizza-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-600" />
            Incasso multiplo
          </DialogTitle>
          <DialogDescription>
            Registra una <strong>singola transazione</strong> (stesso metodo di pagamento) che copre <strong>più polizze</strong>. Il totale sarà calcolato in automatico come somma degli importi delle polizze inserite.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Metodo + urgente + note */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Metodo di pagamento *
              </Label>
              <Select value={metodo} onValueChange={setMetodo}>
                <SelectTrigger data-testid="multi-metodo" className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {methods.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Note (opzionale)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Es. Pagamento cumulativo Bancomat"
                className="h-9"
                data-testid="multi-note"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="multi-urgente"
              checked={urgente}
              onCheckedChange={(v) => setUrgente(!!v)}
              data-testid="multi-urgente"
            />
            <Label htmlFor="multi-urgente" className="text-sm text-slate-700 cursor-pointer">
              Incasso urgente (invia notifica all&apos;agente)
            </Label>
          </div>

          {/* Righe polizze */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                Polizze incassate <span className="text-slate-400">({rows.length})</span>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addRow}
                data-testid="multi-add-row"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi polizza
              </Button>
            </div>
            <div className="divide-y divide-slate-100">
              {rows.map((r, idx) => (
                <div key={r._rid || idx} className="p-3 grid grid-cols-12 gap-2 items-start" data-testid={`multi-row-${idx}`}>
                  <div className="col-span-12 md:col-span-4">
                    <Label className="text-[10px] uppercase text-slate-500">Contraente *</Label>
                    <Input
                      value={r.contraente}
                      onChange={(e) => patchRow(idx, { contraente: e.target.value })}
                      className="h-8 text-sm"
                      data-testid={`multi-contraente-${idx}`}
                    />
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    <Label className="text-[10px] uppercase text-slate-500">
                      N. polizza {polizzaRequired ? "*" : ""}
                    </Label>
                    <Input
                      value={r.numero_polizza}
                      onChange={(e) => patchRow(idx, { numero_polizza: e.target.value })}
                      className="h-8 text-sm"
                      data-testid={`multi-polizza-${idx}`}
                    />
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <Label className="text-[10px] uppercase text-slate-500">Importo *</Label>
                    <AmountInput
                      value={r.importo}
                      onChange={(e) => patchRow(idx, { importo: e.target.value })}
                      placeholder="0,00"
                      className="h-8 text-sm text-right font-mono-num"
                      data-testid={`multi-importo-${idx}`}
                    />
                  </div>
                  {allowAbbuoni && (
                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-[10px] uppercase text-slate-500">Abbuono</Label>
                      <AmountInput
                        value={r.abbuono}
                        onChange={(e) => patchRow(idx, { abbuono: e.target.value })}
                        placeholder="0,00"
                        className="h-8 text-sm text-right font-mono-num"
                        data-testid={`multi-abbuono-${idx}`}
                      />
                    </div>
                  )}
                  <div className={`${allowAbbuoni ? "col-span-6" : "col-span-12"} md:col-span-1 flex md:justify-end`}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRow(idx)}
                      disabled={rows.length <= 2}
                      className="h-8 mt-4 text-red-600 hover:bg-red-50 hover:text-red-700"
                      title={rows.length <= 2 ? "Servono almeno 2 polizze" : "Rimuovi riga"}
                      data-testid={`multi-remove-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {plurimandatari && (
                    <div className="col-span-12 md:col-span-12">
                      <Label className="text-[10px] uppercase text-slate-500">Compagnia *</Label>
                      <Select
                        value={r.compagnia_id || ""}
                        onValueChange={(v) => patchRow(idx, { compagnia_id: v })}
                      >
                        <SelectTrigger className="h-8 text-sm" data-testid={`multi-compagnia-${idx}`}>
                          <SelectValue placeholder="Seleziona compagnia" />
                        </SelectTrigger>
                        <SelectContent>
                          {activeCompanies.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Totale calcolato */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="multi-totale-box">
            <div>
              <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold">
                Totale incasso ({rows.length} polizze)
              </div>
              {allowAbbuoni && totaleAbbuoni > 0 && (
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Abbuoni: {formatEUR(totaleAbbuoni)} · Netto: {formatEUR(totale - totaleAbbuoni)}
                </div>
              )}
            </div>
            <div className="font-heading text-2xl font-bold text-blue-800 font-mono-num" data-testid="multi-totale">
              {formatEUR(totale)}
            </div>
          </div>

          {error && (
            <div
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              data-testid="multi-error"
            >
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            data-testid="multi-cancel"
          >
            Annulla
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!canSubmit || submitting}
            data-testid="multi-submit"
          >
            {submitting ? "Salvataggio..." : `Registra incasso (${formatEUR(totale)})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
